import { DEG, Vec3 } from '../lib/math';

/**
 * When pointing mode should step aside and when it should take over again. Pointing comes first on a
 * phone; a finger drag borrows the view until the phone is aimed somewhere new, and a phone lying flat
 * (on a table, in a lap) stops following, so the screen neither shows the ground nor spins.
 */

/** Aimed this far below the horizon, the phone is lying flat rather than pointing. */
export const FLAT_ALT = -65;
/** Raised above this again, it is being held up. */
export const LIFT_ALT = -40;
/** How long it must lie flat before pointing pauses (a sweep across the ground does not count). */
export const FLAT_SECONDS = 1.2;
/** How long it must be held up before pointing takes over again. */
export const LIFT_SECONDS = 0.3;
/** After a drag, how far the phone must turn from where it was when the finger lifted. */
export const RESUME_TURN = 25;

export type PauseReason = 'drag' | 'flat';

const altOf = (f: Vec3) => Math.asin(Math.max(-1, Math.min(1, f[2]))) / DEG;
const azOf = (f: Vec3) => ((Math.atan2(f[0], f[1]) / DEG) % 360 + 360) % 360;
const angle = (a: Vec3, b: Vec3) => Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))) / DEG;

export class PointingGuard {
  /** Compass direction of the last reading held up, to face when pointing pauses for a flat phone. */
  lastAz = 180;
  private flatSince = -1;
  private liftSince = -1;
  private ref: Vec3 | null = null;

  reset() {
    this.flatSince = this.liftSince = -1;
    this.ref = null;
  }

  /** While pointing: 'flat' once the phone has lain flat for a moment. `t` in seconds. */
  whilePointing(f: Vec3, t: number): 'flat' | null {
    const alt = altOf(f);
    if (alt > LIFT_ALT) this.lastAz = azOf(f);
    if (alt >= FLAT_ALT) {
      this.flatSince = -1;
      return null;
    }
    if (this.flatSince < 0) this.flatSince = t;
    if (t - this.flatSince < FLAT_SECONDS) return null;
    this.reset();
    return 'flat';
  }

  /**
   * While paused: 'resume' when the phone is lifted again (after lying flat) or, after a drag, turned
   * well away from where it pointed when the finger let go. Nothing counts while a finger is down.
   */
  whilePaused(reason: PauseReason, f: Vec3, t: number, touching: boolean): 'resume' | null {
    if (touching) {
      this.ref = null;
      this.liftSince = -1;
      return null;
    }
    const held = altOf(f) > LIFT_ALT;
    if (reason === 'flat') {
      if (!held) {
        this.liftSince = -1;
        return null;
      }
      if (this.liftSince < 0) this.liftSince = t;
      return t - this.liftSince >= LIFT_SECONDS ? 'resume' : null;
    }
    if (!this.ref) {
      this.ref = f;
      return null;
    }
    // Put down flat after a drag: keep the dragged view rather than follow the phone to the floor.
    return held && angle(f, this.ref) > RESUME_TURN ? 'resume' : null;
  }
}
